"use client";
import { useMemo, useRef, useState } from "react";
import type { FxRatesExplorerComponent } from "../../types";
import { ArrowDownRight, ArrowLeftRight, ArrowUpRight, Calendar, ExternalLink, Search, TrendingDown, TrendingUp } from "lucide-react";
import { RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";

type Rate = { code: string; name: string; buy: number; sell: number; change1d: number; change7d: number };

// ----- Site palette -----
// Brand blue + body navy. Same tokens PageHeader, MarketsBoard and
// DocumentCard use, so the converter reads as part of the same site
// rather than a detached widget. Avoids the legacy
// #00A356 / #00935A / #0b3f79 trio that drifted from the brand when this
// page was first scaffolded. No accent orange — the chrome stays in the
// blue family across the renderer.
const COLOR_PRIMARY = "#00B862";
const COLOR_PRIMARY_DARK = "#003e74";
const COLOR_BODY = "#121212";

function toMid(r: Rate): number { return (r.buy + r.sell) / 2; }
/**
 * Locale-aware `toLocaleString`: the site locale maps to a full formatting
 * tag ("fr"→"fr-FR", "en"→"en-GB", anything else passes through as-is). An
 * unknown/invalid tag falls back to fr-FR instead of crashing the render.
 */
function formatNumber(n: number, locale: string, options?: Intl.NumberFormatOptions): string {
  const tag = locale === "fr" ? "fr-FR" : locale === "en" ? "en-GB" : locale;
  try {
    return n.toLocaleString(tag, options);
  } catch {
    return n.toLocaleString("fr-FR", options);
  }
}
/**
 * 4-decimal formatting (e.g. "2 247,0311" in French) — identical to the home
 * MarketsBoard, so the same value reads the same way across pages.
 */
function formatAmount(n: number, locale: string): string {
  return formatNumber(n, locale, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}
/** 2-4 decimal formatting for converter results — keeps small amounts legible. */
function formatConversion(n: number, locale: string): string {
  return formatNumber(n, locale, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

// ---------------------------------------------------------------------------
// MiniSparkline — a tiny inline trend line used inside the rate table rows
// and the secondary-currency chips on the hero. Pure SVG, no axes, no
// tooltip — just the shape. Colour is brand blue on positive delta and
// rose on negative, so the user reads "trend" at a glance.
// ---------------------------------------------------------------------------
function MiniSparkline({ data, color, width = 80, height = 28 }: { data: number[]; color: string; width?: number; height?: number }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data); const max = Math.max(...data);
  const range = Math.max(1e-6, max - min);
  const P = 2;
  const pts = data.map((v, i) => {
    const x = P + (i / (data.length - 1)) * (width - P * 2);
    const y = P + (1 - (v - min) / range) * (height - P * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const d = `M ${pts[0]} L ${pts.slice(1).join(" L ")}`;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true" className="overflow-visible">
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// MainChart — the focused chart for the actively-selected currency in the
// right column. Bigger than the row sparkline; gradient fill underneath,
// hover crosshair with a tooltip pill, dotted gridlines.
// ---------------------------------------------------------------------------
function MainChart({ data, color = COLOR_PRIMARY, height = 220 }: { data: number[]; color?: string; height?: number }) {
  const locale = useLocale();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  if (!data || data.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
        {t(locale, "noHistoryYet")}
      </p>
    );
  }
  const W = 700; const H = height;
  const PL = 52; const PR = 8; const PT = 24; const PB = 24;
  const min = Math.min(...data); const max = Math.max(...data);
  const range = Math.max(1e-6, max - min);
  const points = data.map((v, i) => {
    const x = (i / Math.max(1, data.length - 1)) * (W - PL - PR) + PL;
    const y = H - PB - ((v - min) / range) * (H - PT - PB);
    return { x, y, v };
  });
  const d = `M ${points[0].x},${points[0].y} L ${points.slice(1).map(p => `${p.x},${p.y}`).join(" L ")}`;
  const area = `${d} L ${W - PR},${H - PB} L ${PL},${H - PB} Z`;
  const gridLines = Array.from({ length: 4 }, (_, i) => H - PB - ((i + 1) / 5) * (H - PT - PB));
  const yLabels = [min, min + range * 0.5, max];
  const currentIdx = hoverIdx ?? points.length - 1;
  const current = points[currentIdx];
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect(); if (!rect) return;
    const x = e.clientX - rect.left;
    const t = Math.max(0, Math.min(1, (x - PL) / (rect.width - PL - PR)));
    const idx = Math.round(t * (points.length - 1));
    setHoverIdx(idx);
  };
  const onLeave = () => setHoverIdx(null);
  return (
    <svg ref={svgRef} onMouseMove={onMove} onMouseLeave={onLeave} viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet" role="img" aria-label={t(locale, "rateTrend")}>
      <defs>
        <linearGradient id="fx-explorer-gradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.32" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {gridLines.map((y, idx) => (<line key={idx} x1={PL} y1={y} x2={W - PR} y2={y} stroke="#e2e8f0" strokeDasharray="4 4" />))}
      <line x1={PL} y1={H - PB} x2={W - PR} y2={H - PB} stroke="#cbd5e1" />
      {yLabels.map((val, idx) => {
        const y = H - PB - ((val - min) / range) * (H - PT - PB);
        return <text key={idx} x={8} y={y + 5} fontSize={12} fill="#64748b">{val.toFixed(0)}</text>;
      })}
      <path d={area} fill="url(#fx-explorer-gradient)" />
      <path d={d} stroke={color} strokeWidth="2.5" fill="none" />
      <g pointerEvents="none">
        <line x1={current.x} y1={PT} x2={current.x} y2={H - PB} stroke="#94a3b8" strokeDasharray="4 4" />
        <circle cx={current.x} cy={current.y} r="4.5" fill="#fff" stroke={color} strokeWidth="2.5" />
        <rect x={Math.min(Math.max(current.x - 44, PL), W - PR - 88)} y={PT - 4} width="88" height="22" rx="4" fill={COLOR_BODY} />
        <text x={Math.min(Math.max(current.x, PL + 44), W - PR - 44)} y={PT + 11} fontSize={12} fill="#fff" textAnchor="middle">{formatAmount(current.v, locale)}</text>
      </g>
    </svg>
  );
}

/** Exchange rate explorer renderer with hero, converter, table and chart. */
export function FxRatesExplorerRenderer({ component }: { component: FxRatesExplorerComponent }) {
  const locale = useLocale();
  const content = component.content ?? {};
  const RATES: Rate[] = (content as any).rates ?? [];
  const SERIES: Record<string, number[]> = (content as any).series ?? {};
  // Fallback flag map covering every currency surfaced by the APA snapshot
  // feed (apps.apa.cd/api/exchange-rate). Used when the page payload doesn't
  // carry an explicit `flags` map from the projector — keeps the "Toutes les
  // devises" table from rendering bare codes. XDR (IMF SDR) has no country
  // flag and is intentionally absent.
  const CURRENCY_FLAGS: Record<string, string> = (content as any).flags ?? {
    AED: "ae", AOA: "ao", AUD: "au", BIF: "bi", CAD: "ca", CDF: "cd",
    CHF: "ch", CNY: "cn", EUR: "eu", GBP: "gb", INR: "in", JPY: "jp",
    KWD: "kw", RWF: "rw", SAR: "sa", TZS: "tz", UGX: "ug", USD: "us",
    XAF: "cf", ZAR: "za", ZMW: "zm",
  };
  const defaultFrom = (content as any).defaultFrom ?? "CDF";
  const defaultTo = (content as any).defaultTo ?? "USD";
  const defaultActive = (content as any).defaultActive ?? "USD";
  const asOfDate = ((content as any).asOfDate ?? "").toString();
  // Always render the CTA as an absolute path: this component is
  // rendered from URLs that already nest under `/marche-des-changes/...`,
  // so a relative `marche-des-changes` href would resolve to
  // `/marche-des-changes/marche-des-changes` and 404. We accept whatever
  // the author supplied and prepend `/` when it doesn't already start
  // with `/`, `http://`, or `https://`.
  const detailHref = (() => {
    const raw = ((content as any).detailHref ?? "marche-des-changes").toString().trim();
    if (raw.startsWith("/") || /^https?:\/\//i.test(raw)) return raw;
    return "/" + raw;
  })();
  // ----- CMS-driven text knobs ----- authors override these via the
  // FxRatesExplorer dynamicComponent config; defaults below keep the page
  // sensible when nothing is supplied. Pinning all user-facing copy here
  // means no future tweak needs a code edit. These knobs are widget chrome
  // that never passes the per-field translation pipeline, so a missing value
  // OR one still equal to the French default is swapped per locale; a
  // customized label renders verbatim in every language.
  const localizedKnob = (raw: unknown, frDefault: string, key: Parameters<typeof t>[1]): string => {
    const value = (raw ?? "").toString().trim();
    return !value || value === frDefault ? t(locale, key) : value;
  };
  const heroEyebrow = localizedKnob((content as any).heroEyebrow, "Cours indicatif du jour", "fxHeroEyebrow");
  const converterTitle = localizedKnob((content as any).converterTitle, "Convertisseur", "fxConverterTitle");
  const converterCaption = localizedKnob((content as any).converterCaption, "Taux indicatifs APA", "fxConverterCaption");
  const allCurrenciesTitle = ((content as any).allCurrenciesTitle ?? t(locale, "allCurrencies")).toString();
  const viewAllLabel = ((content as any).viewAllLabel ?? t(locale, "seeAllRates")).toString();
  // Authors can still supply a disclaimer via the CMS; default is empty so
  // pages without an authored value render no disclaimer block.
  const disclaimerText = ((content as any).disclaimerText ?? "").toString();
  // Where the blue conversion-result tile sits on small screens. The CMS
  // controls this so authors can opt into a more prominent layout without
  // a code edit; defaults preserve the legacy behaviour for any older
  // payload that doesn't carry the field.
  //   "card"     — result tile stays inside the converter card (default).
  //   "detached" — result tile renders inside the card on desktop (≥ md)
  //                AND as a standalone block under the card on mobile,
  //                giving the conversion its own visual beat on phones.

  const [q, setQ] = useState("");
  const [amount, setAmount] = useState<number>(1);
  const [from, setFrom] = useState<string>(defaultFrom);
  const [to, setTo] = useState<string>(defaultTo);
  const [activeCode, setActiveCode] = useState<string>(defaultActive);
  const [sortKey, setSortKey] = useState<"code" | "rate">("code");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [chartRange, setChartRange] = useState<"1m" | "3m" | "6m" | "1y">("1y");

  const codeToRate = useMemo(() => {
    return RATES.reduce((acc, r) => { acc[r.code] = r; return acc; }, {} as Record<string, Rate>);
  }, [RATES]);

  // Currency display names: the feed publishes FRENCH names ("Dollar américain").
  // Other locales resolve the ISO code through Intl.DisplayNames (CLDR data),
  // falling back to the feed name when the runtime has no data for that
  // locale/code. French keeps the feed name byte-identical.
  const displayNameByCode = useMemo(() => {
    const out: Record<string, string> = {};
    let displayNames: Intl.DisplayNames | null = null;
    if (locale !== "fr") {
      try {
        displayNames = new Intl.DisplayNames([locale === "en" ? "en-GB" : locale], { type: "currency" });
      } catch {
        displayNames = null;
      }
    }
    for (const r of RATES) {
      let name = r.name;
      if (displayNames) {
        try {
          const resolved = displayNames.of(r.code);
          if (resolved && resolved !== r.code) {
            name = resolved.charAt(0).toUpperCase() + resolved.slice(1);
          }
        } catch { /* unknown code — keep the feed name */ }
      }
      out[r.code] = name;
    }
    return out;
  }, [RATES, locale]);
  const displayName = (r: Rate) => displayNameByCode[r.code] ?? r.name;

  // True when at least one currency has acheteur ≠ vendeur — the rare case
  // where the APA publishes a real bid/ask spread. False (the common case)
  // collapses the column into a single "Cours (CDF)" header.
  const hasBuySellSplit = useMemo(
    () => RATES.some((r) => Math.abs(r.buy - r.sell) > 1e-9),
    [RATES],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const arr = RATES.filter((r) =>
      !term ||
      r.code.toLowerCase().includes(term) ||
      r.name.toLowerCase().includes(term) ||
      displayName(r).toLowerCase().includes(term));
    const dir = sortDir === "asc" ? 1 : -1;
    return [...arr].sort((a, b) => {
      if (sortKey === "code") return a.code.localeCompare(b.code) * dir;
      return (toMid(a) - toMid(b)) * dir;
    });
  }, [q, sortKey, sortDir, RATES, displayNameByCode]);

  function convert(value: number, fromCode: string, toCode: string): number {
    if (fromCode === toCode) return value;
    const fromRate = codeToRate[fromCode];
    const toRate = codeToRate[toCode];
    const fromMid = fromRate ? toMid(fromRate) : 1;
    const toMidV = toRate ? toMid(toRate) : 1;
    const inCDF = fromCode === "CDF" ? value : value * fromMid;
    return toCode === "CDF" ? inCDF : inCDF / toMidV;
  }
  const conversion = useMemo(() => convert(amount, from, to), [amount, from, to]);

  // USD/CDF reference — the headline number on every APA page. Same shape
  // as the home MarketsBoard's "1 USD = ..." card.
  const usdMid = useMemo(() => {
    const usd = codeToRate["USD"]; return usd ? toMid(usd) : 0;
  }, [codeToRate]);
  const usdChange = codeToRate["USD"]?.change1d ?? 0;
  const usdSeries = useMemo(() => (SERIES["USD"] || []).slice(-60), [SERIES]);

  const seriesForActive = useMemo(() => {
    const s = SERIES[activeCode] || [];
    // Approximate trading-day counts: ~22 / month.
    const map = { "1m": 22, "3m": 66, "6m": 132, "1y": 250 } as const;
    return s.slice(-map[chartRange]);
  }, [activeCode, chartRange]);

  /**
   * Picking a currency in the table or a hero chip should do three things
   * at once: focus its chart, *and* pre-fill the converter as "from CDF to
   * <code>" so the visible blue result instantly reflects the user's pick.
   * CDF is the canonical source because Congolese visitors most often ask
   * "how much is N CDF worth in X?" — picking USD does NOT replace CDF on
   * the FROM side, which would loop the converter on itself. Clicking CDF
   * itself (it's not in the table today but might be in future) swaps
   * direction to "from USD to CDF".
   */
  const selectCurrency = (code: string) => {
    setActiveCode(code);
    if (code === "CDF") {
      setFrom("USD");
      setTo("CDF");
    } else {
      setFrom("CDF");
      setTo(code);
    }
  };

  // The hero secondary chips — three currencies after USD with their own
  // mini sparkline. Picked by data order so authors who change `currencies`
  // in the dynamic config get matching chips without further config.
  const secondaryCodes = useMemo(
    () => RATES.filter((r) => r.code !== "CDF" && r.code !== "USD").slice(0, 3),
    [RATES],
  );

  return (
    <>
      {/* ---------------------------------------------------------------- */}
      {/* HERO — soft light-blue surface (#E6F4EA, the same chip tint the  */}
      {/* rest of the site uses) instead of the previous dark gradient.    */}
      {/* Body navy and brand-primary blue carry the typography on top so  */}
      {/* the headline still reads as part of the APA system. Same shade   */}
      {/* on every breakpoint — no breakpoint-dependent palette swap.      */}
      {/* ---------------------------------------------------------------- */}
      <section
        className="mb-8 overflow-hidden rounded-2xl bg-[#E6F4EA] text-[#121212] ring-1 ring-[#00B862]/10"
      >
        <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-[1.5fr_2fr] lg:gap-10">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#00B862]">
              <span>{heroEyebrow}</span>
              {asOfDate ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-[#00B862]/20 bg-white px-2.5 py-0.5 text-[#121212]">
                  <Calendar className="h-3 w-3" aria-hidden="true" /> {asOfDate}
                </span>
              ) : null}
            </div>
            <div className="mt-4">
              <div className="text-sm font-medium text-[#121212]/70">1 USD =</div>
              <div className="mt-1 flex flex-wrap items-baseline gap-3 leading-none">
                <span className="text-4xl font-bold tabular-nums text-[#00B862] sm:text-5xl">{formatAmount(usdMid, locale)}</span>
                <span className="text-lg font-semibold text-[#121212]/70">CDF</span>
              </div>
              {usdSeries.length > 1 ? (
                <div className="mt-4">
                  {/* Brand-blue trend line on the light surface — keeps
                      the hero in the APA blue family while reading
                      clearly against the soft background. */}
                  <MiniSparkline data={usdSeries} color={COLOR_PRIMARY} width={260} height={36} />
                </div>
              ) : null}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-[#00B862]">{t(locale, "otherCurrencies")}</div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {secondaryCodes.map((r) => {
                const up = r.change1d >= 0;
                const Trend = up ? TrendingUp : TrendingDown;
                const series = (SERIES[r.code] || []).slice(-60);
                const selected = activeCode === r.code;
                return (
                  <button
                    type="button"
                    key={r.code}
                    onClick={() => selectCurrency(r.code)}
                    aria-pressed={selected}
                    className={`group rounded-lg border px-3 py-3 text-left transition focus:outline-none focus:ring-2 focus:ring-[#00B862]/40 ${selected ? "border-[#00B862] bg-white" : "border-[#00B862]/15 bg-white/70 hover:bg-white"}`}
                  >
                    <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-[#00B862]">
                      <span>{r.code}/CDF</span>
                      <Trend className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <div className="mt-1 text-lg font-semibold tabular-nums text-[#121212]">{formatAmount(toMid(r), locale)}</div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className={`text-xs font-medium ${up ? "text-emerald-600" : "text-rose-600"}`}>
                        {up ? "+" : ""}{(r.change1d * 100).toFixed(2)} %
                      </span>
                      {series.length > 1 ? (
                        <MiniSparkline data={series} color={up ? "#059669" : "#e11d48"} width={64} height={20} />
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* PRIMARY ACTION — converter + chart, side by side on desktop;     */}
      {/* on mobile the chart sits on TOP (order-1) so users see the trend */}
      {/* for the active currency before the inputs, and the converter     */}
      {/* form follows directly underneath (order-2).                       */}
      {/* ---------------------------------------------------------------- */}
      <section className="mb-8 grid gap-4 lg:grid-cols-2">
        <div className="order-2 lg:order-1">
          <ConverterCard
            title={converterTitle}
            caption={converterCaption}
            rates={RATES}
            amount={amount}
            from={from}
            to={to}
            conversion={conversion}
            onAmount={setAmount}
            onFrom={setFrom}
            onTo={setTo}
            onSwap={() => { const f = from; setFrom(to); setTo(f); }}
          />
        </div>
        <div className="order-1 lg:order-2">
          <ChartCard
            activeCode={activeCode}
            series={seriesForActive}
            chartRange={chartRange}
            onRangeChange={setChartRange}
            changePct={codeToRate[activeCode]?.change1d ?? 0}
          />
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* ALL CURRENCIES — search + table (desktop) / cards (mobile) with  */}
      {/* a mini sparkline per row so users see the trend at a glance.     */}
      {/* ---------------------------------------------------------------- */}
      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-[#121212]">{allCurrenciesTitle}</h2>
          <RuntimeLink
            href={detailHref}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[#00B862]/30 bg-[#E6F4EA] px-3 text-xs font-semibold uppercase tracking-wide text-[#00B862] transition hover:bg-[#d6ecdc]"
          >
            {viewAllLabel} <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </RuntimeLink>
        </div>
        <div className="mb-3 relative">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-10 w-full rounded-md border border-slate-300 bg-white pl-10 pr-3 text-sm text-[#121212] shadow-sm placeholder:text-slate-400 focus:border-[#00B862] focus:outline-none focus:ring-1 focus:ring-[#00B862]"
            placeholder={t(locale, "searchCurrencyPlaceholder")}
            aria-label={t(locale, "searchCurrency")}
          />
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        </div>

        {/* Desktop / tablet table — adds an inline sparkline column. */}
        <div className="hidden md:block overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                  onClick={() => { setSortKey("code"); setSortDir((d) => d === "asc" ? "desc" : "asc"); }}
                >
                  Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">{t(locale, "currencyHeader")}</th>
                {hasBuySellSplit ? (
                  <>
                    <th
                      className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                      onClick={() => { setSortKey("rate"); setSortDir((d) => d === "asc" ? "desc" : "asc"); }}
                    >
                      {t(locale, "midHeader")}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">{t(locale, "buyHeader")}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">{t(locale, "sellHeader")}</th>
                  </>
                ) : (
                  <th
                    className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                    onClick={() => { setSortKey("rate"); setSortDir((d) => d === "asc" ? "desc" : "asc"); }}
                  >
                    {t(locale, "rateCdfHeader")}
                  </th>
                )}
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">{t(locale, "oneDayHeader")}</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">{t(locale, "trend60Header")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {filtered.map((r) => {
                const up1 = r.change1d >= 0;
                const Icon1 = up1 ? ArrowUpRight : ArrowDownRight;
                const flag = CURRENCY_FLAGS[r.code];
                const selected = activeCode === r.code;
                const series = (SERIES[r.code] || []).slice(-60);
                return (
                  <tr
                    key={r.code}
                    className={`cursor-pointer transition ${selected ? "bg-[#E6F4EA]" : "hover:bg-slate-50"}`}
                    onClick={() => selectCurrency(r.code)}
                    aria-selected={selected}
                  >
                    <td className="px-4 py-3 text-sm font-semibold text-[#00B862]">{r.code}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      <span className="inline-flex items-center gap-2">{flag && <span className={`fi fi-${flag}`}></span>} {displayName(r)}</span>
                    </td>
                    {hasBuySellSplit ? (
                      <>
                        <td className="px-4 py-3 text-right font-mono text-sm font-semibold tabular-nums text-[#121212]">{formatAmount(toMid(r), locale)}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-[#121212]">{formatAmount(r.buy, locale)}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-[#121212]">{formatAmount(r.sell, locale)}</td>
                      </>
                    ) : (
                      <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-[#121212]">{formatAmount(toMid(r), locale)}</td>
                    )}
                    <td className={`px-4 py-3 text-right text-sm whitespace-nowrap ${up1 ? "text-emerald-600" : "text-rose-600"}`}>
                      <span className="inline-flex items-center gap-1"><Icon1 className="h-3.5 w-3.5" />{Math.abs(r.change1d * 100).toFixed(2)} %</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {series.length > 1 ? (
                        <span className="inline-block align-middle">
                          <MiniSparkline data={series} color={up1 ? "#059669" : "#e11d48"} width={92} height={26} />
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={hasBuySellSplit ? 7 : 5} className="px-4 py-8 text-center text-sm text-slate-500">{t(locale, "noResults")}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked cards (table is unreadable narrow). */}
        <div className="md:hidden grid grid-cols-1 gap-3">
          {filtered.map((r) => {
            const up1 = r.change1d >= 0;
            const Icon1 = up1 ? ArrowUpRight : ArrowDownRight;
            const flag = CURRENCY_FLAGS[r.code];
            const selected = activeCode === r.code;
            const series = (SERIES[r.code] || []).slice(-60);
            return (
              <button
                key={r.code}
                onClick={() => selectCurrency(r.code)}
                className={`w-full rounded-lg border bg-white p-4 text-left transition ${selected ? "border-[#00B862] bg-[#E6F4EA]" : "border-slate-200 hover:border-[#00B862]/40"}`}
                aria-pressed={selected}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {flag && <span className={`fi fi-${flag}`}></span>}
                    <div className="font-semibold text-[#00B862]">{r.code}</div>
                  </div>
                  <div className="text-xs text-slate-500">{displayName(r)}</div>
                </div>
                <div className="mt-3 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">{t(locale, "rateCdfHeader")}</div>
                    <div className="mt-1 font-mono text-lg font-semibold tabular-nums text-[#121212]">{formatAmount(toMid(r), locale)}</div>
                  </div>
                  {series.length > 1 ? (
                    <MiniSparkline data={series} color={up1 ? "#059669" : "#e11d48"} width={80} height={28} />
                  ) : null}
                </div>
                <div className={`mt-2 inline-flex items-center gap-1 text-xs ${up1 ? "text-emerald-600" : "text-rose-600"}`}>
                  <Icon1 className="h-4 w-4" />{Math.abs(r.change1d * 100).toFixed(2)} {t(locale, "percentOneDay")}
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">{t(locale, "noResults")}</div>
          )}
        </div>
      </section>

      {disclaimerText ? <p className="mt-6 text-xs text-slate-500">{disclaimerText}</p> : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// ConverterCard — promoted from the right-side aside to a first-class panel.
// Title + caption come from CMS so authors can rename without code edits.
// The previous accent-orange left rail was removed at the user's request —
// against the otherwise all-blue chrome it read as a stray yellow stripe.
// The brand-blue result tile at the bottom stays as the visual anchor.
// ---------------------------------------------------------------------------
function ConverterCard(props: {
  title: string;
  caption: string;
  rates: Rate[];
  amount: number;
  from: string;
  to: string;
  conversion: number;
  onAmount: (n: number) => void;
  onFrom: (s: string) => void;
  onTo: (s: string) => void;
  onSwap: () => void;
}) {
  const { title, caption, rates, amount, from, to, conversion, onAmount, onFrom, onTo, onSwap } = props;
  const locale = useLocale();
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <header className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-[#121212]">{title}</h2>
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{caption}</span>
      </header>
      <div className="mt-4 grid gap-3">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t(locale, "amount")}</span>
          <input
            type="number"
            className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-lg font-semibold text-[#121212] outline-none focus:border-[#00B862] focus:ring-1 focus:ring-[#00B862]"
            value={amount}
            min={0}
            onChange={(e) => onAmount(Number(e.target.value))}
          />
        </label>
        <div className="flex flex-nowrap items-end gap-2 w-full">
          <label className="block min-w-0 flex-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t(locale, "fromCurrency")}</span>
            <select
              name="from"
              aria-label={t(locale, "sourceCurrency")}
              value={from}
              onChange={(e) => onFrom(e.target.value)}
              className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-[#121212] outline-none focus:border-[#00B862] focus:ring-1 focus:ring-[#00B862]"
            >
              {/* CDF is the local currency and always a valid converter
                  endpoint, but the projector emits foreign rates only
                  (since you can't quote CDF in CDF). Inject it as a
                  synthetic first option so users can pick it on either
                  side of the converter. */}
              {!rates.some((r) => r.code === "CDF") ? (
                <option value="CDF">CDF</option>
              ) : null}
              {rates.map((r) => (<option key={r.code} value={r.code}>{r.code}</option>))}
            </select>
          </label>
          <button
            type="button"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 transition hover:border-[#00B862] hover:text-[#00B862]"
            onClick={onSwap}
            aria-label={t(locale, "swapCurrencies")}
          >
            <ArrowLeftRight className="h-4 w-4" />
          </button>
          <label className="block min-w-0 flex-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t(locale, "toCurrency")}</span>
            <select
              name="to"
              aria-label={t(locale, "targetCurrency")}
              value={to}
              onChange={(e) => onTo(e.target.value)}
              className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-[#121212] outline-none focus:border-[#00B862] focus:ring-1 focus:ring-[#00B862]"
            >
              {/* CDF is the local currency and always a valid converter
                  endpoint, but the projector emits foreign rates only
                  (since you can't quote CDF in CDF). Inject it as a
                  synthetic first option so users can pick it on either
                  side of the converter. */}
              {!rates.some((r) => r.code === "CDF") ? (
                <option value="CDF">CDF</option>
              ) : null}
              {rates.map((r) => (<option key={r.code} value={r.code}>{r.code}</option>))}
            </select>
          </label>
        </div>
        {/* Inline brand-blue result tile — anchored to the bottom of the
            converter card on every breakpoint. */}
        <ResultPanel amount={amount} from={from} to={to} conversion={conversion} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ResultPanel — the brand-blue conversion-result tile. Extracted from the
// converter card so it can also be rendered as a detached mobile block
// without duplicating the markup.
// ---------------------------------------------------------------------------
function ResultPanel({ amount, from, to, conversion }: { amount: number; from: string; to: string; conversion: number }) {
  const locale = useLocale();
  return (
    <div
      className="grid place-items-center rounded-md px-4 py-4 text-white"
      style={{ background: `linear-gradient(135deg, ${COLOR_PRIMARY} 0%, ${COLOR_PRIMARY_DARK} 100%)` }}
    >
      <div className="text-center">
        <div className="text-xs font-semibold uppercase tracking-wide text-white/80">
          {formatNumber(amount, locale)} {from} {t(locale, "equalsTo")}
        </div>
        <div className="mt-1">
          <span className="font-mono text-2xl font-bold tabular-nums">{formatConversion(conversion, locale)}</span>{" "}
          <span className="text-base text-white/80">{to}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChartCard — focused chart for the actively-selected currency, range
// toggles top-right, 1-day delta badge so the user sees the headline number
// without reading axes.
// ---------------------------------------------------------------------------
function ChartCard(props: {
  activeCode: string;
  series: number[];
  chartRange: "1m" | "3m" | "6m" | "1y";
  onRangeChange: (r: "1m" | "3m" | "6m" | "1y") => void;
  changePct: number;
}) {
  const { activeCode, series, chartRange, onRangeChange, changePct } = props;
  const locale = useLocale();
  const up = changePct >= 0;
  const Trend = up ? TrendingUp : TrendingDown;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[#121212]">{t(locale, "trendOf")} {activeCode}/CDF</h2>
          <div className={`mt-1 inline-flex items-center gap-1 text-xs font-medium ${up ? "text-emerald-600" : "text-rose-600"}`}>
            <Trend className="h-3.5 w-3.5" aria-hidden="true" />
            {up ? "+" : ""}{(changePct * 100).toFixed(2)} {t(locale, "percentOneDay")}
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {(["1m", "3m", "6m", "1y"] as const).map((r) => (
            <button
              key={r}
              onClick={() => onRangeChange(r)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                chartRange === r
                  ? "border-[#00B862] bg-[#00B862] text-white"
                  : "border-slate-300 text-slate-700 hover:border-[#00B862]/40"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </header>
      <div className="mt-4">
        <MainChart data={series} />
      </div>
    </section>
  );
}
